import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Shield, Heart, Users, Calendar, CreditCard, Repeat } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
export function MembershipPage() {
  const standardBenefits = ['Full access to all KMA UK events and seminars', 'Professional networking opportunities with senior consultants', 'Voting rights in association elections and decisions', 'Participation in charitable initiatives and medical camps', 'Access to mentorship programs', 'Exclusive member newsletter and updates', 'Discounted rates for annual gala and special events'];
  const retiredBenefits = ['Community involvement and social connection', 'Access to all KMA UK events and gatherings', 'Continued professional networking', 'Participation in charitable work', 'Member newsletter and updates', 'Recognition of your years of service'];
  return <>
      <SEO title="Membership" description="Join KMA UK. Two membership tiers with flexible payment options to suit your needs." data-id="element-554" />

      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20 md:py-24" data-id="element-555">
        <div className="container mx-auto px-4 text-center" data-id="element-556">
          <FadeIn data-id="element-557">
            <Users className="h-12 w-12 mx-auto mb-6 text-primary-200" data-id="element-558" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-559">
              Join KMA UK
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto leading-relaxed" data-id="element-560">
              Become part of a distinguished network of medical professionals
              committed to excellence, community, and humanitarian service.
            </p>
          </FadeIn>
        </div>
      </div>

      <Section data-id="element-561">
        <div className="max-w-6xl mx-auto" data-id="element-562">
          {/* Membership Tiers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-563">
            {/* Standard Membership */}
            <FadeIn delay={0} data-id="element-564">
              <Card className="border-2 border-primary-200 shadow-xl relative overflow-hidden h-full flex flex-col" data-id="element-565">
                <div className="absolute top-0 right-0 bg-primary-600 text-white px-4 py-1 text-sm font-bold" data-id="element-566">
                  Most Popular
                </div>
                <CardHeader className="text-center pb-6 pt-8" data-id="element-567">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto" data-id="element-568">
                    <Users className="h-8 w-8" data-id="element-569" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-570">
                    Standard Membership
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-571">
                    For active medical professionals
                  </CardDescription>
                  <div className="mt-6" data-id="element-572">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-573">£30</div>
                    <div className="text-gray-500 mt-1" data-id="element-574">per year</div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col" data-id="element-575">
                  <div className="flex-1" data-id="element-576">
                    <h4 className="font-bold text-gray-900 mb-4" data-id="element-577">
                      Benefits Include:
                    </h4>
                    <ul className="space-y-3 mb-8" data-id="element-578">
                      {standardBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-579">
                          <Check className="h-5 w-5 text-green-600 mr-2 shrink-0 mt-0.5" data-id="element-580" />
                          <span className="text-gray-700" data-id="element-581">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-3 pt-6 border-t" data-id="element-582">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4" data-id="element-583">
                      <CreditCard className="h-4 w-4" data-id="element-584" />
                      <span className="font-medium" data-id="element-585">
                        Choose your payment method:
                      </span>
                    </div>

                    <Button className="w-full" size="lg" data-id="element-586">
                      <Repeat className="mr-2 h-5 w-5" data-id="element-587" />
                      Join - Yearly Direct Debit
                    </Button>

                    <Button variant="outline" className="w-full" size="lg" data-id="element-588">
                      <CreditCard className="mr-2 h-5 w-5" data-id="element-589" />
                      Join - One-off Annual Payment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>

            {/* Retired Membership */}
            <FadeIn delay={0.1} data-id="element-590">
              <Card className="border-2 border-accent-200 shadow-xl h-full flex flex-col" data-id="element-591">
                <CardHeader className="text-center pb-6 pt-8" data-id="element-592">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto" data-id="element-593">
                    <Heart className="h-8 w-8" data-id="element-594" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-595">
                    Retired Membership
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-596">
                    For retired medical professionals
                  </CardDescription>
                  <div className="mt-6" data-id="element-597">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-598">£15</div>
                    <div className="text-gray-500 mt-1" data-id="element-599">per year</div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col" data-id="element-600">
                  <div className="flex-1" data-id="element-601">
                    <h4 className="font-bold text-gray-900 mb-4" data-id="element-602">
                      Benefits Include:
                    </h4>
                    <ul className="space-y-3 mb-8" data-id="element-603">
                      {retiredBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-604">
                          <Check className="h-5 w-5 text-green-600 mr-2 shrink-0 mt-0.5" data-id="element-605" />
                          <span className="text-gray-700" data-id="element-606">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-3 pt-6 border-t" data-id="element-607">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4" data-id="element-608">
                      <CreditCard className="h-4 w-4" data-id="element-609" />
                      <span className="font-medium" data-id="element-610">
                        Choose your payment method:
                      </span>
                    </div>

                    <Button variant="secondary" className="w-full" size="lg" data-id="element-611">
                      <Repeat className="mr-2 h-5 w-5" data-id="element-612" />
                      Join - Yearly Direct Debit
                    </Button>

                    <Button variant="outline" className="w-full" size="lg" data-id="element-613">
                      <CreditCard className="mr-2 h-5 w-5" data-id="element-614" />
                      Join - One-off Annual Payment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>
          </div>

          {/* Reassurance Section */}
          <FadeIn delay={0.2} data-id="element-615">
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200" data-id="element-616">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-617">
                <div className="flex flex-col items-center" data-id="element-618">
                  <div className="h-12 w-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center mb-3" data-id="element-619">
                    <Shield className="h-6 w-6" data-id="element-620" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-621">
                    Secure Payments
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-622">
                    All transactions are encrypted and processed securely
                    through Stripe.
                  </p>
                </div>

                <div className="flex flex-col items-center" data-id="element-623">
                  <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mb-3" data-id="element-624">
                    <Repeat className="h-6 w-6" data-id="element-625" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-626">
                    Cancel Anytime
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-627">
                    No long-term commitment. Cancel your membership at any time
                    with no penalties.
                  </p>
                </div>

                <div className="flex flex-col items-center" data-id="element-628">
                  <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-3" data-id="element-629">
                    <Heart className="h-6 w-6" data-id="element-630" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-631">
                    Support Charity
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-632">
                    Your membership directly supports our humanitarian and
                    charitable initiatives.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Why Join Section */}
      <Section background="gray" data-id="element-633">
        <div className="max-w-4xl mx-auto" data-id="element-634">
          <FadeIn className="text-center mb-12" data-id="element-635">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-636">
              Why Join KMA UK?
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-637">
              Membership offers you a unique platform to connect, learn, and
              give back to communities in need.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-638">
            {[{
            icon: Users,
            title: 'Professional Network',
            desc: 'Connect with senior consultants, GPs, and specialists across the UK.'
          }, {
            icon: Calendar,
            title: 'Exclusive Events',
            desc: 'Annual gala dinners, CPD seminars, and social gatherings throughout the year.'
          }, {
            icon: Heart,
            title: 'Humanitarian Impact',
            desc: 'Participate in medical camps, disaster relief, and community health projects.'
          }, {
            icon: Users,
            title: 'Mentorship',
            desc: 'Guide junior doctors and medical students through career development programs.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-639">
                <Card className="h-full border-none shadow-md" data-id="element-640">
                  <CardContent className="p-6 flex items-start gap-4" data-id="element-641">
                    <div className="h-12 w-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0" data-id="element-642">
                      <item.icon className="h-6 w-6" data-id="element-643" />
                    </div>
                    <div data-id="element-644">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-645">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-646">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* FAQ / Contact Section */}
      <Section className="text-center" data-id="element-647">
        <FadeIn data-id="element-648">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-649">
            Have Questions?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-650">
            If you have any questions about membership or need assistance with
            your application, our team is here to help.
          </p>
          <Link to="/contact" data-id="element-651">
            <Button variant="outline" size="lg" data-id="element-652">
              Contact Membership Secretary
            </Button>
          </Link>
        </FadeIn>
      </Section>
    </>;
}